<template>
    <div>
        <div
            :class="{ backdrop: langMenuOpen }"
            @click="closeOnOutsideClick"
        />
        <div
            id="langchange"
            role="navigation"
            :aria-label="$t('App.LanguageNavigation.Heading')"
        >
            <section
                id="lang-section"
                class="langChange"
            >
                <div
                    id="langHeader"
                    ref="langButton"
                    role="button"
                    :aria-label="langMenuLabel"
                    :aria-expanded="isMenuOpen.toString()"
                    tabindex="0"
                    :class="[ 'currentLang', langMenuOpen ? 'open' : 'closed' ]"
                    @click="toggleLangMenu"
                    @keydown.enter="toggleLangMenu"
                    @keydown.space.prevent="toggleLangMenu"
                    @keydown.esc.prevent="hideLinks"
                    @keydown.tab="handleTab"
                >
                    {{ activeLangString }}
                </div>
                <div
                    v-show="langMenuOpen"
                    class="languages"
                >
                    <ul>
                        <li
                            :aria-current="activeLang === 'fi'"
                            :class="{ active: activeLang === 'fi' }"
                        >
                            <a
                                class="langlink fi"
                                tabindex="0"
                                @click="changeLang('fi')"
                                @keyup.enter="changeLang('fi')"
                                @keydown.space.prevent="changeLang('fi')"
                                @keydown.esc="hideLinks"
                            >{{ $t("App.Languages.Finland", "fi") }}</a>
                        </li>
                        <li
                            lang="sv"
                            :aria-current="activeLang === 'sv'"
                            :class="{ active: activeLang === 'sv' }"
                        >
                            <a
                                class="langlink sv"
                                tabindex="0"
                                @click="changeLang('sv')"
                                @keyup.enter="changeLang('sv')"
                                @keydown.space.prevent="changeLang('sv')"
                                @keydown.esc="hideLinks"
                            >{{ $t("App.Languages.Sweden", "sv") }}</a>
                        </li>
                        <li
                            lang="en"
                            :aria-current="activeLang === 'en'"
                            :class="{ active: activeLang === 'en' }"
                        >
                            <a
                                class="langlink en"
                                tabindex="0"
                                @click="changeLang('en')"
                                @keyup.enter="changeLang('en')"
                                @keydown.space.prevent="changeLang('en')"
                                @keydown.esc="hideLinks"
                                @keydown.tab="handleLastItemTab"
                            >{{ $t("App.Languages.England", "en") }}</a>
                        </li>
                    </ul>
                </div>
            </section>
            <div
                id="isMenuOpen"
                aria-live="polite"
                class="sr-only"
            />
        </div>
    </div>
</template>

<script lang="ts">
    import { Vue, Component } from "vue-facing-decorator"
    import { store } from "@/store/store"
    import ApiService from "../services/ApiService"
    import utils from "../utils/utils"

    @Component
    export default class LangChange extends Vue {
        apiService: ApiService = new ApiService(store.getters.baseHref)
        langMenuOpen: boolean = false

        mounted() {
            // Tarkistetaan initialisoinnin j�lkeen sivun senhetkinen kieli ja p�ivitet��n koko sivun lang-attribuutti
            if (this.activeLang) {
                document.getElementsByTagName("html")[0].lang = this.activeLang
            }
        }

        get activeLang(): string | undefined {
            return store.getters.activeLang
        }

        get activeLangString() {
            switch (store.getters.activeLang) {
                case "fi":
                    return this.$t("App.Languages.Finland", "fi")
                case "sv":
                    return this.$t("App.Languages.Sweden", "sv")
                case "en":
                    return this.$t("App.Languages.England", "en")
                default:
            }
        }

        get langMenuLabel(): string | undefined {
            const active = this.activeLang ?? "fi"
            return this.$t("App.LangChange.LabelInAllLanguages", active)
        }

        get isMenuOpen(): boolean {
            if (this.langMenuOpen) {
                return true
            }
            return false
        }

        changeLang(lang: string) {
            if (lang === "fi" || lang === "sv" || lang === "en") {
                store.dispatch("setLang", lang)
                this.$i18n.locale = lang
                this.langMenuOpen = false
                this.apiService.changeLanguage(lang).then(() => {
                    if (this.$route) {
                        const currentPageName = this.$route.params.pageName as string
                        utils.changePageTitle(undefined, currentPageName)
                    }
                    window.location.reload()
                })
            }
        }

        toggleLangMenu() {
            this.langMenuOpen = !this.langMenuOpen
        }

        closeOnOutsideClick(event: any): void {
            if (this.langMenuOpen && event.target.className === "backdrop") {
                this.langMenuOpen = !this.langMenuOpen
            }
        }

        handleTab(event: KeyboardEvent) {
            if (event.shiftKey && this.langMenuOpen) {
                // Shift+Tab => sulje kielivalikko
                this.langMenuOpen = !this.langMenuOpen
            }
        }

        handleLastItemTab(event: KeyboardEvent) {
            if (!event.shiftKey && this.langMenuOpen) {
                // Shift => sulje kielivalikko
                this.langMenuOpen = !this.langMenuOpen
            }
        }

        hideLinks(): void {
            if (!this.langMenuOpen) {
                this.$emit("closeHamburgerMenu")
            }
            else {
                (this.$refs.langButton as HTMLButtonElement).focus()
                this.langMenuOpen = false
            }
        }
    }

</script>

<style scoped>

    #langchange {
        flex-grow: 1;
        text-align: end;
        display: flex;
        align-items: center;
        height: 100%;
        justify-content: end;
        -webkit-justify-content: flex-end;
        align-items: flex-end;
        -webkit-align-items: flex-end;
        z-index: 22;
    }
        #langchange .languages li a:focus {
            outline: 3px solid white;
            outline-offset: 2px;
        }

    #langHeader:focus {
        outline: 3px solid white;
        outline-offset: -12px;
    }

    div.hamburger #langchange {
        justify-content: start;
    }

    #langchange section.langChange {
        height: 100%;
    }

    #langchange section.langChange div.languages {
        text-align: left;
        z-index: 10000;
    }

    @media (max-width: 1099px) {
        #langchange section.langChange div.languages {
            background-color: #FFFFFF;
        }

            #langchange section.langChange div.languages a {
                color: var(--Traficom-main-color);
            }
        #langchange .languages li a:focus {
            outline: 3px solid var(--Traficom-main-color);
            outline-offset: 2px;
        }
    }

    #langchange div.languages a.langlink {
        cursor: pointer;
    }

    #langchange section.langChange > div.currentLang.closed {
        color: #FFFFFF;
        align-items: center;
        display: flex;
        height: 100%;
    }

    section.langChange > div.currentLang.open {
        color: #FFFFFF;
        display: flex;
        height: 100%;
        align-items: center;
        background-color: var(--Traficom-main-color);
    }

    #langchange section.langChange > div.currentLang::before {
        filter: invert(1);
    }

    #langchange section.langChange > div.currentLang::after {
        filter: invert(1);
    }

    .backdrop {
        display: flex;
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
    }
</style>
